import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ContentData } from '@/types/content';
import initialContent from '@/data/content.json';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Json } from '@/integrations/supabase/types';

interface ContentContextType {
  // Draft content (what admin edits)
  content: ContentData;
  setContent: React.Dispatch<React.SetStateAction<ContentData>>;
  updateContent: (path: string, value: unknown) => void;

  // Published content (what visitors see)
  publishedContent: ContentData;

  // Actions
  saveDraft: (contentOverride?: ContentData) => Promise<void>;
  publish: (contentOverride?: ContentData) => Promise<void>;
  discardDraft: () => void;

  // Status
  isSaving: boolean;
  isPublishing: boolean;
  isLoading: boolean;
  lastSaved: Date | null;
  lastPublished: Date | null;
  hasUnpublishedChanges: boolean;
  isPublished: boolean;
}

const ContentContext = createContext<ContentContextType | undefined>(undefined);

// Helper to get admin auth token
const getAuthToken = (): string | null => {
  const savedSession = localStorage.getItem('admin_session');
  if (!savedSession) return null;
  
  try {
    const session = JSON.parse(savedSession);
    return session.token;
  } catch {
    return null;
  }
};

// Helper to make authenticated admin API calls
const adminCmsUpdate = async (data: {
  content_key?: string;
  content_data?: unknown;
  published_data?: unknown;
  is_published?: boolean;
  published_at?: string;
}): Promise<void> => {
  const token = getAuthToken();
  
  // If we have a token, use the secure edge function
  if (token) {
    const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-operations`);
    url.searchParams.set('entity', 'cms');
    url.searchParams.set('action', 'update');

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Request failed: ${response.status}`);
    }
  } else {
    // Fallback: Try direct Supabase call (will fail if RLS blocks it, but needed for initial setup)
    const { error } = await supabase
      .from('cms_content')
      .update({
        content_data: JSON.parse(JSON.stringify(data.content_data)) as Json,
        published_data: data.published_data ? JSON.parse(JSON.stringify(data.published_data)) as Json : undefined,
        is_published: data.is_published,
        published_at: data.published_at,
      })
      .eq('content_key', data.content_key || 'main');
    
    if (error) throw error;
  }
};

export const ContentProvider = ({ children }: { children: ReactNode }) => {
  const [content, setContent] = useState<ContentData>(initialContent as ContentData);
  const [publishedContent, setPublishedContent] = useState<ContentData>(initialContent as ContentData);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [lastPublished, setLastPublished] = useState<Date | null>(null);
  const [isPublished, setIsPublished] = useState(false);
  const [originalDraft, setOriginalDraft] = useState<string>('');
  const [hasPublishedData, setHasPublishedData] = useState(false);

  // Check if there are unpublished changes
  // If never published before, always show as having unpublished changes
  const hasUnpublishedChanges = !hasPublishedData || JSON.stringify(content) !== JSON.stringify(publishedContent);

  // Load content from database on mount
  useEffect(() => {
    const loadContent = async () => {
      try {
        const { data, error } = await supabase
          .from('cms_content')
          .select('content_data, published_data, updated_at, published_at, is_published')
          .eq('content_key', 'main')
          .maybeSingle();

        if (error) {
          console.error('Error loading content:', error);
          return;
        }

        if (data) {
          // Load draft content
          if (data.content_data) {
            const draftContent = {
              ...initialContent,
              ...(data.content_data as object)
            } as ContentData;
            setContent(draftContent);
            setOriginalDraft(JSON.stringify(draftContent));
          }

          // Load published content
          if (data.published_data) {
            const pubContent = {
              ...initialContent,
              ...(data.published_data as object)
            } as ContentData;
            setPublishedContent(pubContent);
            setHasPublishedData(true);
          } else {
            // No published data yet - keep initial content as published
            // but mark that we haven't published yet
            setHasPublishedData(false);
          }

          setLastSaved(data.updated_at ? new Date(data.updated_at) : null);
          setLastPublished(data.published_at ? new Date(data.published_at) : null);
          setIsPublished(data.is_published || false);
        }
      } catch (err) {
        console.error('Failed to load content:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, []);

  const updateContent = (path: string, value: unknown) => {
    setContent(prev => {
      const newContent = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let current: Record<string, unknown> = newContent;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (current[keys[i]] === undefined) {
          current[keys[i]] = {};
        }
        current = current[keys[i]] as Record<string, unknown>;
      }
      
      current[keys[keys.length - 1]] = value;
      return newContent;
    });
  };

  // Save and publish immediately (for all content)
  const saveDraft = async (contentOverride?: ContentData) => {
    setIsSaving(true);
    try {
      const dataToSave = contentOverride ?? content;
      const now = new Date().toISOString();

      await adminCmsUpdate({
        content_key: 'main',
        content_data: dataToSave,
        published_data: dataToSave,
        is_published: true,
        published_at: now,
      });

      setPublishedContent(dataToSave);
      setLastSaved(new Date());
      setLastPublished(new Date());
      setIsPublished(true);
      setHasPublishedData(true);
      setOriginalDraft(JSON.stringify(dataToSave));

      toast({
        title: 'Gespeichert & veröffentlicht! ✓',
        description: 'Die Änderungen sind jetzt live.',
      });
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: 'Speichern fehlgeschlagen',
        description: 'Bitte versuche es erneut.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Publish (makes draft live)
  const publish = async (contentOverride?: ContentData) => {
    setIsPublishing(true);
    try {
      const dataToSave = contentOverride ?? content;
      const now = new Date().toISOString();

      await adminCmsUpdate({
        content_key: 'main',
        content_data: dataToSave,
        published_data: dataToSave,
        is_published: true,
        published_at: now,
      });

      setPublishedContent(dataToSave);
      setLastPublished(new Date());
      setLastSaved(new Date());
      setIsPublished(true);
      setHasPublishedData(true);
      setOriginalDraft(JSON.stringify(dataToSave));

      toast({
        title: 'Veröffentlicht! 🎉',
        description: 'Die Änderungen sind jetzt live.',
      });
    } catch (error) {
      console.error('Publish error:', error);
      toast({
        title: 'Veröffentlichung fehlgeschlagen',
        description: 'Bitte versuche es erneut.',
        variant: 'destructive',
      });
    } finally {
      setIsPublishing(false);
    }
  };

  // Discard draft changes and revert to published version
  const discardDraft = () => {
    setContent(publishedContent);
    toast({ 
      title: "Änderungen verworfen", 
      description: "Der Entwurf wurde auf die veröffentlichte Version zurückgesetzt." 
    });
  };

  return (
    <ContentContext.Provider value={{ 
      content, 
      setContent, 
      updateContent,
      publishedContent,
      saveDraft,
      publish,
      discardDraft,
      isSaving,
      isPublishing,
      isLoading,
      lastSaved,
      lastPublished,
      hasUnpublishedChanges,
      isPublished
    }}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

// Hook for frontend pages - only uses published content
export const usePublishedContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('usePublishedContent must be used within a ContentProvider');
  }
  return context.publishedContent;
};
